import { useState, useEffect } from 'react'
import { collection, query, onSnapshot, doc, setDoc, updateDoc, arrayUnion, Timestamp, getDoc, orderBy } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { LiveChat, LiveChatMessage } from '@/types/models'

/**
 * Hook for managing all live chats (admin view)
 */
export function useLiveChats() {
  const [liveChats, setLiveChats] = useState<LiveChat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    try {
      const liveChatsRef = collection(db, 'liveChats')
      const liveChatsQuery = query(liveChatsRef, orderBy('lastMessageAt', 'desc'))

      const unsubscribe = onSnapshot(
        liveChatsQuery,
        (snapshot) => {
          const chatsData: LiveChat[] = snapshot.docs.map(doc => {
            const data = doc.data()
            return {
              id: doc.id,
              userId: data.userId,
              createdAt: data.createdAt?.toDate() || new Date(),
              messages: (data.messages || []).map((msg: any) => ({
                content: msg.content,
                senderUsername: msg.senderUsername,
                timestamp: msg.timestamp?.toDate() || new Date(),
                isAdmin: msg.isAdmin || false
              })),
              lastMessageAt: data.lastMessageAt?.toDate() || new Date(),
              lastMessageIsFromUser: data.lastMessageIsFromUser || false
            }
          })
          
          setLiveChats(chatsData)
          setLoading(false)
        },
        (err) => {
          console.error('[LiveChat] Error fetching live chats:', err)
          setError(err as Error)
          setLoading(false)
        }
      )

      return () => unsubscribe()
    } catch (err) {
      console.error('[LiveChat] Error setting up live chats listener:', err)
      setError(err as Error)
      setLoading(false)
    }
  }, [])

  return { liveChats, loading, error }
}

/**
 * Hook for managing a single live chat (user or admin view)
 */
export function useLiveChat(userId: string | undefined) {
  const [liveChat, setLiveChat] = useState<LiveChat | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    try {
      const liveChatRef = doc(db, 'liveChats', userId)

      const unsubscribe = onSnapshot(
        liveChatRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data()
            const chatData: LiveChat = {
              id: snapshot.id,
              userId: data.userId,
              createdAt: data.createdAt?.toDate() || new Date(),
              messages: (data.messages || []).map((msg: any) => ({
                content: msg.content,
                senderUsername: msg.senderUsername,
                timestamp: msg.timestamp?.toDate() || new Date(),
                isAdmin: msg.isAdmin || false
              })),
              lastMessageAt: data.lastMessageAt?.toDate() || new Date(),
              lastMessageIsFromUser: data.lastMessageIsFromUser || false
            }
            setLiveChat(chatData)
          } else {
            setLiveChat(null)
          }
          setLoading(false)
        },
        (err) => {
          console.error('[LiveChat] Error fetching live chat:', err)
          setError(err as Error)
          setLoading(false)
        }
      )

      return () => unsubscribe()
    } catch (err) {
      console.error('[LiveChat] Error setting up live chat listener:', err)
      setError(err as Error)
      setLoading(false)
    }
  }, [userId])

  return { liveChat, loading, error }
}

/**
 * Send a message in a live chat
 */
export async function sendLiveChatMessage(
  userId: string,
  message: string,
  senderUsername: string,
  isAdmin: boolean,
  userInstagram?: string
): Promise<void> {
  const liveChatRef = doc(db, 'liveChats', userId)
  
  const newMessage: LiveChatMessage = {
    content: message,
    senderUsername,
    timestamp: new Date(),
    isAdmin
  }

  const messageData = {
    content: newMessage.content,
    senderUsername: newMessage.senderUsername,
    timestamp: Timestamp.now(),
    isAdmin: newMessage.isAdmin
  }

  try {
    // Check if chat exists
    const chatDoc = await getDoc(liveChatRef)
    
    if (chatDoc.exists()) {
      // Update existing chat
      await updateDoc(liveChatRef, {
        messages: arrayUnion(messageData),
        lastMessageAt: Timestamp.now(),
        lastMessageIsFromUser: !isAdmin
      })
    } else {
      // Create new chat
      await setDoc(liveChatRef, {
        userId,
        createdAt: Timestamp.now(),
        messages: [messageData],
        lastMessageAt: Timestamp.now(),
        lastMessageIsFromUser: !isAdmin,
        userInstagram: userInstagram || '' // Store user's instagram handle for reference
      })
    }
  } catch (error) {
    console.error('[LiveChat] Error sending live chat message:', error)
    throw error
  }
}

